import { currentPhase } from '@nutians/shared';
import type {
  ConsentsResponse,
  CycleInsightsResponse,
  CycleLogsResponse,
  CyclePhaseResponse,
  CycleSymptoms,
  EnergyAvailabilitySignalResponse,
} from '@nutians/shared/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';

export const CYCLE_KEY = ['cycle'] as const;
export const CONSENTS_KEY = ['consents'] as const;

export function todayLocalDate(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

export function useCycleConsent() {
  return useQuery<ConsentsResponse>({
    queryKey: CONSENTS_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/consents');
      if (error || !data) throw new Error('GET /me/consents failed');
      return data as ConsentsResponse;
    },
    staleTime: 60_000,
  });
}

export function useUpdateCycleConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (granted: boolean) => {
      if (!granted) {
        const { error } = await api.DELETE('/me/cycle');
        if (error) throw new Error('DELETE /me/cycle failed');
        return null;
      }
      const { data, error } = await api.PUT('/me/consents', {
        body: {
          surface: 'settings_web',
          consents: [{ purpose: 'cycle', granted: true }],
        },
      });
      if (error || !data) throw new Error('PUT /me/consents failed');
      return data;
    },
    onSuccess: (data, granted) => {
      if (data) qc.setQueryData(CONSENTS_KEY, data);
      void qc.invalidateQueries({ queryKey: CONSENTS_KEY });
      void qc.invalidateQueries({ queryKey: CYCLE_KEY });
      if (!granted) void qc.invalidateQueries({ queryKey: ['weight'] });
    },
  });
}

export function useCycleLogs(enabled: boolean) {
  return useQuery<CycleLogsResponse>({
    queryKey: [...CYCLE_KEY, 'logs'],
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/cycle');
      if (error || !data) throw new Error('GET /me/cycle failed');
      return data as CycleLogsResponse;
    },
  });
}

export function useCycleInsights(enabled: boolean) {
  return useQuery<CycleInsightsResponse>({
    queryKey: [...CYCLE_KEY, 'insights'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/cycle/insights', {});
      if (error || !data) throw new Error('GET /me/cycle/insights failed');
      return data as unknown as CycleInsightsResponse;
    },
  });
}

/**
 * Energy-availability signal (T19.6 / ADR-0030 §5 — high-care, ED-gated).
 * Disabled by default server-side (double lock); `active` is almost always
 * false. The card only renders when the server says so.
 */
export function useEnergySignal(enabled: boolean) {
  return useQuery<EnergyAvailabilitySignalResponse>({
    queryKey: [...CYCLE_KEY, 'energy-signal'],
    enabled,
    staleTime: Number.POSITIVE_INFINITY, // surface once per session; cap is server-side
    queryFn: async () => {
      const { data, error } = await api.GET('/me/cycle/energy-signal', {});
      if (error || !data) throw new Error('GET /me/cycle/energy-signal failed');
      return data as unknown as EnergyAvailabilitySignalResponse;
    },
  });
}

export function useOptOutEnergySignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/me/cycle/energy-signal/opt-out', {});
      if (error) throw new Error('POST /me/cycle/energy-signal/opt-out failed');
      return null;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...CYCLE_KEY, 'energy-signal'] });
    },
  });
}

export function useCyclePhase(enabled: boolean, userSetLengthDays: number) {
  return useQuery<CyclePhaseResponse>({
    queryKey: [...CYCLE_KEY, 'phase', userSetLengthDays],
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/cycle/phase', {
        params: { query: { userSetLengthDays } },
      });
      if (error || !data) throw new Error('GET /me/cycle/phase failed');
      return data as CyclePhaseResponse;
    },
  });
}

export function useCreateCycleLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      eventType: 'period_start' | 'period_end';
      occurredOn: string;
      symptoms: CycleSymptoms | null;
    }) => {
      const { data, error } = await api.POST('/me/cycle', { body: input });
      if (error || !data) throw new Error('POST /me/cycle failed');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CYCLE_KEY });
    },
  });
}

export function phaseForDate(
  logs: CycleLogsResponse | undefined,
  asOf: string,
  userSetLengthDays: number,
) {
  return currentPhase(
    (logs?.logs ?? []).map((log) => ({ eventType: log.eventType, occurredOn: log.occurredOn })),
    asOf,
    userSetLengthDays,
  );
}
